/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import Image from 'next/image';
import { useState } from 'react';
import ButtonLinkItemList from './ButtonLinkItemList';
import ImageModal from './ImageModal';

export default function AuthorProfileInformationHeading({
  profile,
}: {
  profile: any;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 lg:gap-12 mb-4 sm:mb-6">
      <div
        className={`relative group ${profile.image ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {/* Sophisticated Animated Glow */}
        <div className="absolute -inset-4 bg-gradient-to-r from-primary/30 via-blue-500/30 to-purple-600/30 rounded-[4rem] blur-3xl opacity-0 group-hover:opacity-100 transition-all duration-1000"></div>

        {/* Premium Gradient Border Wrapper */}
        <div
          className="relative p-[3px] rounded-[2.8rem] bg-gradient-to-tr from-primary/50 via-white/20 to-blue-400/50 shadow-2xl transition-transform duration-700 group-hover:scale-[1.02] active:scale-[0.98]"
          onClick={() => profile.image && setIsModalOpen(true)}
        >
          <div className="relative w-36 h-36 sm:w-44 sm:h-44 md:w-52 md:h-52 rounded-[2.5rem] overflow-hidden glass border border-white/5 flex items-center justify-center">
            {profile.image ? (
              <Image
                src={profile.image}
                alt={profile.name}
                fill
                sizes="(min-width: 768px) 208px, (min-width: 640px) 176px, 144px"
                className="object-cover transition-transform duration-700 group-hover:scale-110"
                loading="eager"
                fetchPriority="high"
                unoptimized
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground/50">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span className="text-[10px] uppercase tracking-widest font-bold">
                  No Image
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center lg:items-start gap-4 w-full lg:w-auto lg:flex-1 min-w-0">
        {profile.showAvailability && (
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span
              className="text-[10px] uppercase tracking-[0.2em] font-bold text-green-500/90"
              dangerouslySetInnerHTML={{ __html: profile.availability || '' }}
              suppressHydrationWarning
            />
          </div>
        )}
        <div className="flex flex-col items-center lg:items-start w-full lg:w-auto min-w-0">
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-gradient leading-[1.05] mb-4 sm:mb-5 lg:mb-6 text-center lg:text-left break-words max-w-full"
            dangerouslySetInnerHTML={{ __html: profile.name || '' }}
            suppressHydrationWarning
          />
          <div className="w-full lg:w-auto">
            <ButtonLinkItemList profile={profile} />
          </div>
        </div>
      </div>

      <ImageModal
        src={profile.imageFullUrl || profile.image}
        alt={profile.name}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
